/* eslint-disable react/display-name */
import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import { useQuery, useMutation, useSubscription, gql } from '@apollo/client'
import DataTable from '../components/DataTable'
import {
  getActiveOrders,
  getRidersByZone,
  subscriptionOrder,
  updateStatus,
  assignRider
} from '../apollo'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import { transformToNewline } from '../utils/stringManipulations'
import SearchBar from '../components/TableHeader/SearchBar'
import useGlobalStyles from '../utils/globalStyles'
import { wideTableStyles } from '../utils/tableCustomStyles'
import StatusBadge from '../components/StatusBadge'
import { Container, MenuItem, Select, Box, useTheme } from '@mui/material'
import TableHeader from '../components/TableHeader'
import PrepTimeCountdown from '../components/Order/PrepTimeCountdown'
import OrderNotes from '../components/Order/OrderNotes'
import { NotificationContainer, NotificationManager } from 'react-notifications'
import 'react-notifications/lib/notifications.css'
import AcceptOrderDialog from '../components/Order/AcceptOrderDialog'

const SUBSCRIPTION_ORDER = gql`
  ${subscriptionOrder}
`
const UPDATE_STATUS = gql`
  ${updateStatus}
`
const ASSIGN_RIDER = gql`
  ${assignRider}
`
const GET_RIDERS_BY_ZONE = gql`
  ${getRidersByZone}
`
const GET_ACTIVE_ORDERS = gql`
  ${getActiveOrders}
`

const Orders = props => {
  const theme = useTheme()
  const { t } = props
  const [searchQuery, setSearchQuery] = useState('')
  const onChangeSearch = e => setSearchQuery(e.target.value)
  const [mutateUpdate, { loading: updating }] = useMutation(UPDATE_STATUS)
  // Order waiting on the preparation-time dialog before it is accepted
  const [accepting, setAccepting] = useState(null)
  const globalClasses = useGlobalStyles()
  const [mutateAssign] = useMutation(ASSIGN_RIDER)

  const riderFunc = row => {
    const { data: dataZone } = useQuery(GET_RIDERS_BY_ZONE, {
      variables: { id: row.zone._id }
    })
    const riders = (dataZone && dataZone.ridersByZone) || []
    const assignedId = row.rider ? row.rider._id : ''

    return (
      <Select
        id="input-rider"
        name="input-rider"
        value={assignedId}
        displayEmpty
        inputProps={{ 'aria-label': t('Rider') }}
        // The assigned rider is shown by the control itself, so an order that
        // has one does not need a separate name above the dropdown.
        renderValue={id => {
          const assigned = riders.find(rider => rider._id === id)
          const label =
            (row.rider && row.rider.name) || (assigned && assigned.name)
          return (
            <span style={{ color: label ? undefined : '#94A3B8' }}>
              {label || t('Unassigned')}
            </span>
          )
        }}
        className={globalClasses.selectInput}
        sx={{ height: 34, fontSize: 13, width: '100%' }}>
        <MenuItem value="" disabled>
          {t('Unassigned')}
        </MenuItem>
        {riders.map(rider => (
          <MenuItem
            onClick={() => {
              mutateAssign({
                variables: {
                  id: row._id,
                  riderId: rider._id
                },
                onCompleted: data => {
                  console.error('Mutation success data:', data)
                  NotificationManager.success(
                    'Successful',
                    'Rider updated!',
                    3000
                  )
                },
                onError: error => {
                  console.error('Mutation error:', error)
                  NotificationManager.error(
                    'Error',
                    'Failed to update rider!',
                    3000
                  )
                }
              })
            }}
            value={rider._id}
            key={rider._id}>
            {rider.name}
          </MenuItem>
        ))}
      </Select>
    )
  }
  const {
    data: dataOrders,
    error: errorOrders,
    loading: loadingOrders,
    refetch: refetchOrders
  } = useQuery(GET_ACTIVE_ORDERS, { pollInterval: 3000 })

  const statusFunc = row => {
    const handleStatusSuccessNotification = status => {
      NotificationManager.success(status, 'Status Updated!', 3000)
    }

    const changeStatus = (orderStatus, notifyAs, time) => {
      if (
        orderStatus === 'ACCEPTED' &&
        time === undefined &&
        row.deliveryType !== 'scheduled'
      ) {
        setAccepting({ row, changeStatus })
        return
      }
      mutateUpdate({
        variables: {
          id: row._id,
          orderStatus,
          time
        },
        onCompleted: data => {
          handleStatusSuccessNotification(notifyAs)
          refetchOrders()
        },
        onError: error => {
          console.error('Mutation error:', error)
          NotificationManager.error('Error', 'Failed to update status!', 3000)
        }
      })
    }

    // Which moves are open from here, unchanged from the previous per-item
    // handlers: only a pending order can be accepted, and an order still in
    // flight can be rejected or marked delivered.
    const options = []
    if (row.orderStatus === 'PENDING') {
      options.push({
        value: 'ACCEPTED',
        label: t('Accept'),
        notifyAs: 'ACCEPTED'
      })
    }
    if (
      ['PENDING', 'ACCEPTED', 'PICKED', 'ASSIGNED'].includes(row.orderStatus)
    ) {
      options.push({
        value: 'CANCELLED',
        label: t('Reject'),
        notifyAs: 'REJECTED'
      })
      options.push({
        value: 'DELIVERED',
        label: t('Delivered'),
        notifyAs: 'DELIVERED'
      })
    }

    return (
      <Select
        id="input-status"
        name="input-status"
        inputProps={{ 'aria-label': t('Status') }}
        value={row.orderStatus}
        onChange={event => {
          const next = options.find(o => o.value === event.target.value)
          if (next) changeStatus(next.value, next.notifyAs)
        }}
        renderValue={status => <StatusBadge status={status} />}
        className={globalClasses.selectInput}
        sx={{
          height: 34,
          fontSize: 13,
          width: '100%',
          '& .MuiSelect-select': { display: 'flex', alignItems: 'center' }
        }}>
        {/* The current state, so the control has a matching value to display */}
        <MenuItem value={row.orderStatus} disabled>
          {t(row.orderStatus)}
        </MenuItem>
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    )
  }
  const subscribeFunc = row => {
    const { data: dataSubscription } = useSubscription(SUBSCRIPTION_ORDER, {
      variables: { id: row._id }
    })
    console.log(dataSubscription)
    return (
      <div style={{ overflow: 'hidden', whiteSpace: 'pre-wrap' }}>
        {row.orderId}
        <br />
        {transformToNewline(row.deliveryAddress.deliveryAddress, 3)}
      </div>
    )
  }
  const columns = [
    {
      name: t('OrderInformation'),
      minWidth: '170px',
      wrap: true,
      sortable: true,
      selector: 'orderId',
      cell: row => subscribeFunc(row)
    },
    {
      name: t('RestaurantCol'),
      minWidth: '110px',
      wrap: true,
      selector: 'restaurant.name'
    },
    {
      name: t('Name'),
      minWidth: '110px',
      wrap: true,
      selector: 'user.name',
      cell: row => row?.user?.name || 'N/A'
    },
    {
      name: t('Phone'),
      minWidth: '135px',
      wrap: true,
      selector: 'user.phone',
      cell: row => row?.user?.phone || 'N/A'
    },
    {
      name: t('Payment'),
      minWidth: '95px',
      wrap: true,
      selector: 'paymentMethod'
    },
    {
      name: t('Type'),
      minWidth: '95px',
      wrap: true,
      selector: 'isPickedUp',
      cell: row => (row.isPickedUp ? 'Pickup' : 'Delivery')
    },
    {
      // A finished order has no move left to make, so it reads as a badge;
      // otherwise the badge sits inside the control that changes it.
      name: t('Status'),
      minWidth: '170px',
      selector: 'orderStatus',
      cell: row =>
        ['CANCELLED', 'DELIVERED'].includes(row.orderStatus) ? (
          <StatusBadge status={row.orderStatus} />
        ) : (
          statusFunc(row)
        )
    },
    {
      // Courier orders are not ours to assign, and a picked-up or finished
      // order is settled, so those show the rider as plain text. Everything
      // else gets the assignment control, which names the current rider itself.
      name: t('Rider'),
      minWidth: '170px',
      wrap: true,
      selector: 'rider',
      cell: row => {
        if (row.deliveryMode === 'courier') {
          return <span>{t('Courier')}</span>
        }
        const assignable =
          !row.isPickedUp &&
          !['CANCELLED', 'DELIVERED'].includes(row.orderStatus)
        if (!assignable) {
          return <span>{row.rider ? row.rider.name : 'N/A'}</span>
        }
        return riderFunc(row)
      }
    },
    {
      name: t('StoreNote'),
      selector: 'orderNotes',
      minWidth: '150px',
      wrap: true,
      cell: row => <OrderNotes order={row} type="store" />
    },
    {
      name: t('RiderNote'),
      selector: 'riderNotes',
      minWidth: '150px',
      wrap: true,
      cell: row => <OrderNotes order={row} type="rider" />
    },
    {
      name: t('PrepTime'),
      minWidth: '165px',
      wrap: true,
      selector: 'preparationTime',
      cell: row => <PrepTimeCountdown order={row} />
    },
    {
      name: t('OrderTime'),
      minWidth: '115px',
      wrap: true,
      cell: row => (
        <>{new Date(row.createdAt).toLocaleString().replace(/ /g, '\n')}</>
      )
    }
  ]

  const conditionalRowStyles = [
    {
      when: row => ['DELIVERED', 'CANCELLED'].includes(row.orderStatus),
      style: {
        backgroundColor: theme.palette.success.dark
      }
    }
  ]
  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null

  const filtered =
    searchQuery.length < 3
      ? dataOrders && dataOrders.getActiveOrders
      : dataOrders &&
        dataOrders.getActiveOrders.filter(order => {
          return (
            order?.restaurant?.name?.toLowerCase().search(regex) > -1 ||
            order?.orderId?.toLowerCase().search(regex) > -1 ||
            order?.deliveryAddress?.deliveryAddress
              ?.toLowerCase()
              .search(regex) > -1 ||
            order?.orderId?.toLowerCase().search(regex) > -1 ||
            order?.paymentMethod?.toLowerCase().search(regex) > -1 ||
            order?.orderStatus?.toLowerCase().search(regex) > -1 ||
            (order?.user?.name
              ? order?.user?.name.toLowerCase().search(regex) > -1
              : false) ||
            (order?.user?.phone
              ? order?.user?.phone.toLowerCase().search(regex) > -1
              : false) ||
            (order?.rider !== null
              ? order?.rider?.name.toLowerCase().search(regex) > -1
              : false)
          )
        })

  return (
    <>
      <NotificationContainer />
      <AcceptOrderDialog
        open={Boolean(accepting)}
        loading={updating}
        onClose={() => setAccepting(null)}
        onConfirm={time => {
          accepting.changeStatus('ACCEPTED', 'ACCEPTED', time)
          setAccepting(null)
        }}
      />
      <Header />
      <Container className={globalClasses.flex} fluid>
        {errorOrders ? (
          <tr>
            <td>
              `${'Error'}! ${errorOrders.message}`
            </td>
          </tr>
        ) : null}
        {loadingOrders ? (
          <CustomLoader />
        ) : (
          <DataTable
            subHeader={true}
            subHeaderComponent={
              <SearchBar
                value={searchQuery}
                onChange={onChangeSearch}
                onClick={() => refetchOrders()}
              />
            }
            title={<TableHeader title={t('Dispatch')} />}
            columns={columns}
            data={filtered}
            progressPending={loadingOrders}
            pointerOnHover
            progressComponent={<CustomLoader />}
            pagination
            conditionalRowStyles={conditionalRowStyles}
            customStyles={wideTableStyles(1690)}
            selectableRows
          />
        )}
      </Container>
    </>
  )
}
export default withTranslation()(Orders)
